import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { BookingApi, PublicApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { Field, PageHeader } from "../../components/common";
import { LocationPicker, Place } from "../../components/LocationPicker";
import { ServiceNotice } from "./ServiceNotice";
import { kmBetween, peso } from "../../lib/format";

const PURPOSES = ["Check-up / consultation", "Dialysis", "Hospital discharge", "Inter-hospital transfer", "Therapy session", "Other"];

export default function ScheduleTrip() {
  const nav = useNavigate();
  const [pickup, setPickup] = useState<Place | null>(null);
  const [dest, setDest] = useState<Place | null>(null);
  const [when, setWhen] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: cfg } = useQuery({ queryKey: ["public-config"], queryFn: PublicApi.config });
  const { data: avail } = useQuery({
    queryKey: ["availability", pickup?.lat, pickup?.lng],
    queryFn: () => PublicApi.availability(pickup.lat, pickup.lng), enabled: !!pickup,
  });
  const km = kmBetween(pickup, dest);
  const estimate = cfg && km != null ? cfg.fares.scheduled_base + Math.round(km * cfg.fares.per_km) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr("");
    if (!pickup || !dest || !when) { setErr("Set pickup, destination and date/time."); return; }
    setBusy(true);
    try {
      const b = await BookingApi.create({ type: "scheduled", pickup, destination: dest, scheduled_at: `${when}:00+08:00`, condition: purpose, notes });
      nav(`/patient/bookings/${b.id}?confirmed=1`, { replace: true });
    } catch (ex) { setErr(errorMessage(ex)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="schedule-form">
      <PageHeader title="Schedule medical transport" subtitle="Non-emergency trips. Times are Philippine Standard Time (UTC+8)." />
      <div className="fp-card"><LocationPicker label="Pickup" value={pickup} onChange={setPickup} testId="schedule-pickup" /></div>
      {pickup && avail && <ServiceNotice data={avail} />}
      <div className="fp-card"><LocationPicker label="Destination (hospital / clinic)" value={dest} onChange={setDest} allowGps={false} kind="destination" testId="schedule-destination" /></div>
      <div className="fp-card space-y-4">
        <Field label="Date & time (PHT)"><input className="fp-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-datetime-input" /></Field>
        <Field label="Purpose">
          <select className="fp-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="schedule-purpose-select">
            {PURPOSES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Notes (mobility needs, wheelchair, companion)"><textarea className="fp-input h-20 py-2" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="schedule-notes-input" /></Field>
        {estimate != null && (
          <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="schedule-fare-estimate">
            Estimated fare: <strong>{peso(estimate)}</strong> ({km.toFixed(1)} km straight-line){cfg.sample_values && " · sample pricing"}. Final fare is confirmed by the server; membership discounts apply at payment.
          </p>
        )}
      </div>
      {err && <p role="alert" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="schedule-error">{err}</p>}
      <button className="fp-btn-primary h-14 w-full text-base" disabled={busy || (avail && !avail.in_service_area)} data-testid="schedule-submit-button">
        {busy && <Loader2 className="animate-spin" size={16} />} Book scheduled transport
      </button>
    </form>
  );
}
